import { ReactNode, RefObject } from 'react';

import { Button, DialogState, Drawer, Link } from '@components/primitives';
import { cn } from '@components/primitives/utilities';

import { CheckoutActivity } from '@components/features/AlgunaCheckout/useAlgunaCheckout';
import LayoutLoader from '@components/features/Layout/LayoutLoader';
import { labelForPlan } from '@components/features/Settings/TeamBilling/utils';

import { CheckoutStatus, CheckoutStatusVariant } from './CheckoutStatus';
import { ShimmerLogo } from './ShimmerLogo';
import { UpgradeFeatures } from './UpgradeFeatures';

import css from './styles.module.css';

type UpgradeDrawerProps = {
  state: DialogState;
  trigger?: ReactNode;
  children: ReactNode;
  dismissible?: boolean;
};

type UpgradeDrawerBodyProps = {
  scrollRef?: RefObject<HTMLDivElement>;
  title?: string;
  dismissible?: boolean;
  activity?: CheckoutActivity;
  onDismiss: () => void;
} & (
  | {
      completed?: false;
      children: ReactNode;
      subText?: string;
      canSubmit: boolean;
      isProcessing: boolean;
      onSubmit: () => void;
    }
  | { completed: true; variant?: CheckoutStatusVariant }
);

const DEFAULT_TITLE = 'Upgrade plan';

export function UpgradeDrawer({
  state,
  trigger,
  children,
  dismissible,
}: UpgradeDrawerProps) {
  return (
    <Drawer state={state} trigger={trigger} dismissible={dismissible}>
      {children}
    </Drawer>
  );
}

export function UpgradeDrawerBody({
  scrollRef,
  title = DEFAULT_TITLE,
  dismissible = true,
  activity = 'idle',
  onDismiss,
  ...props
}: UpgradeDrawerBodyProps) {
  const busy = activity !== 'idle';

  return (
    <>
      <Drawer.Content className={css.drawer}>
        <Drawer.Header>
          <ShimmerLogo />
          <Drawer.Title>{title}</Drawer.Title>
          <Drawer.Close
            className={cn(css.close, !dismissible && css.closeHidden)}
          />
        </Drawer.Header>

        <div className={css.panes}>
          <div
            aria-busy={busy}
            className={css.checkoutColumn}
            data-busy={busy || undefined}
          >
            <div className={css.busy} role="status">
              <LayoutLoader />
              <span className="sr-only">
                {activity === 'confirming'
                  ? 'Processing payment'
                  : 'Loading checkout'}
              </span>
            </div>
            <div
              ref={scrollRef}
              className={css.checkoutScroll}
              {...({ inert: busy ? '' : undefined } as { inert?: string })}
            >
              <div className={css.checkoutPane}>
                {props.completed ? (
                  <div className={css.status}>
                    <CheckoutStatus variant={props.variant} />
                  </div>
                ) : (
                  <>
                    {props.subText && (
                      <p className={css.subText}>{props.subText}</p>
                    )}
                    {props.children}
                  </>
                )}
              </div>
            </div>

            <Drawer.Footer className={css.footer}>
              {props.completed ? (
                <Button variant="primary" onClick={onDismiss}>
                  Close
                </Button>
              ) : (
                <Button
                  variant="primary"
                  disabled={!props.canSubmit}
                  loading={props.isProcessing}
                  onClick={props.onSubmit}
                >
                  Upgrade
                </Button>
              )}
            </Drawer.Footer>
          </div>

          <aside className={css.summaryPane}>
            <h4 className={css.paneHeading}>Summary</h4>
            {/* TODO: pull plan name and price from Alguna instead of hardcoding. */}
            <dl className={css.lines}>
              <div>
                <dt>{labelForPlan('pro')}</dt>
                <dd>$995.00</dd>
              </div>
              <div>
                <dt>Billed</dt>
                <dd>Monthly</dd>
              </div>
              <div className={css.total}>
                <dt>Total</dt>
                <dd>$995.00</dd>
              </div>
            </dl>
            <p className={css.usageNote}>
              Usage is billed on top of the monthly plan at the rates below.
            </p>
            <UpgradeFeatures />
            <Link
              className={css.pricingLink}
              href="https://evervault.com/pricing"
              target="_blank"
            >
              See full pricing<span aria-hidden> {'->'}</span>
            </Link>
          </aside>
        </div>
      </Drawer.Content>
    </>
  );
}
